// React
import { useEffect, useState } from "react";

// Third-party
import { useLocation, useNavigate } from "react-router-dom";
import { applyActionCode, checkActionCode } from "firebase/auth";

// Hooks & Contexts
import { useTranslation } from "@/hooks";
import { callResendEmailVerification } from "@/contexts/authVerify";

// Lib
import { auth } from "@/lib/firebase";

// Config
import { env } from "@/config/env";

// Constants
import { AppRoutes } from "@/constants";

type VerifyStatus =
  | "checking"
  | "success"
  | "verified_redirect"
  | "error"
  | "missing_code";

export default function VerifyEmailPage() {
  const t = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const oobCode = params.get("oobCode");
  // Firebase's mode parameter tells us this came from a verification action
  const mode = params.get("mode");

  const [status, setStatus] = useState<VerifyStatus>("checking");
  const [errorType, setErrorType] = useState<"expired" | "invalid" | "generic">(
    "generic",
  );
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      // If no oobCode:
      // - Firebase emulator verifies and redirects WITHOUT passing it
      // - Just show success since verification should be complete
      // - If user navigated directly without verifying, login will tell them
      if (!oobCode) {
        if (mounted) {
          setStatus("verified_redirect");
        }
        return;
      }
      try {
        // Check the action code to get email info
        const info = await checkActionCode(auth, oobCode);
        if (mounted && info.data?.email) {
          setUserEmail(info.data.email);
        }

        // Apply the verification
        await applyActionCode(auth, oobCode);
        if (mounted) {
          setStatus("success");
        }
      } catch (e) {
        if (mounted) {
          // Determine error type - Firebase errors have a code property
          const code = (e as { code?: string })?.code || "";
          if (code === "auth/expired-action-code") {
            setStatus("error");
            setErrorType("expired");
          } else if (code === "auth/invalid-action-code") {
            // Invalid code often means it was already used (verification completed)
            // Show as success since user can likely sign in
            setStatus("verified_redirect");
          } else {
            setStatus("error");
            setErrorType("generic");
          }
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [oobCode, mode]);

  const handleResendVerification = async () => {
    if (!userEmail || resending) return;

    setResending(true);
    setResendError(false);
    setResendSuccess(false);

    try {
      // Call backend to resend verification email
      await callResendEmailVerification(env.apiBaseUrl, userEmail);
      setResendSuccess(true);
    } catch {
      setResendError(true);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6">
        {status === "success" || status === "verified_redirect" ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
            <h1 className="text-xl font-semibold text-emerald-800 mb-2">
              {t("emailConfirmedTitle")}
            </h1>
            <p className="text-emerald-700 mb-4">{t("emailConfirmedBody")}</p>
            <button
              className="w-full bg-red-600 hover:bg-red-700 text-white rounded-md py-3"
              onClick={() => navigate(AppRoutes.login)}
            >
              {t("logIntoAccount")}
            </button>
          </div>
        ) : status === "checking" ? (
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-4" />
            <p className="text-gray-700">{t("pleaseWait")}</p>
          </div>
        ) : status === "missing_code" ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h1 className="text-xl font-semibold text-yellow-800 mb-2">
              {t("verificationMissingCodeTitle")}
            </h1>
            <p className="text-yellow-700 mb-4">
              {t("verificationMissingCodeBody")}
            </p>
            <button
              className="w-full bg-red-600 hover:bg-red-700 text-white rounded-md py-3"
              onClick={() => navigate(AppRoutes.login)}
            >
              {t("backToLogin")}
            </button>
          </div>
        ) : (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h1 className="text-xl font-semibold text-red-800 mb-2">
              {t("verificationFailedTitle")}
            </h1>
            <p className="text-red-700 mb-4">
              {errorType === "expired"
                ? t("verificationExpired")
                : errorType === "invalid"
                  ? t("verificationInvalid")
                  : t("verificationFailed")}
            </p>

            {/* Resend option for expired links */}
            {userEmail && errorType === "expired" && (
              <div className="mb-4 p-4 bg-white rounded border border-red-100">
                <p className="text-sm text-gray-700 mb-3">
                  {(t as unknown as (k: string, v: string) => string)(
                    "resendVerificationHint",
                    userEmail,
                  )}
                </p>
                {resendSuccess ? (
                  <p className="text-sm text-emerald-600 font-medium">
                    {t("verificationResent")}
                  </p>
                ) : resendError ? (
                  <p className="text-sm text-red-600 font-medium mb-2">
                    {t("resendFailed")}
                  </p>
                ) : null}
                {!resendSuccess && (
                  <button
                    onClick={handleResendVerification}
                    disabled={resending}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md py-2 text-sm disabled:opacity-50"
                  >
                    {resending ? t("pleaseWait") : t("resendVerificationEmail")}
                  </button>
                )}
              </div>
            )}

            <button
              className="w-full bg-red-600 hover:bg-red-700 text-white rounded-md py-3"
              onClick={() => navigate(AppRoutes.login)}
            >
              {t("backToLogin")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
